import React, { useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import NotePdfHeader, {
  buildNotePdfHeader,
  buildNotePdfFooter
} from './NotePdfHeader';

// US Letter at 96dpi (8.5in = 816px), same page as the backend note PDF.
const RENDER_WIDTH = 816;

const S = {
  page: {
    width: RENDER_WIDTH,
    background: '#fff',
    // Only horizontal padding here; the PDF page margin (marginY) supplies the
    // top/bottom spacing uniformly across all pages.
    padding: '0 72px',
    color: '#525E6F',
    fontFamily: "'Krub', sans-serif",
    fontWeight: 400,
    fontSize: '11.5px',
    lineHeight: 1.5,
    boxSizing: 'border-box'
  },
  sectionH: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#1A2332',
    margin: '0 0 9px',
    padding: '0 0 6px 10px',
    borderLeft: '3px solid #47A4EA',
    borderBottom: '1px solid #E8ECF0'
  },
  sectionBody: {
    fontSize: '11.5px',
    lineHeight: 1.58,
    color: '#525E6F',
    margin: 0,
    padding: 0
  },
  footer: {
    marginTop: 34,
    paddingTop: 10,
    borderTop: '1px solid #E8ECF0',
    fontSize: '8.5px',
    color: '#A0AAB4',
    display: 'flex',
    justifyContent: 'space-between'
  }
};

/**
 * Headless generator for the EMR Note PDF (design: emr-note-pdf.html). Used for
 * the "Note PDF" attachment when /clinic/job/attachments returns no file link.
 * Renders the note off-screen from data already loaded in the detail panel,
 * snapshots it with html2canvas, builds a paged PDF and loads it into the tab.
 */
const NoteEmrPdf = ({
  header,
  patient,
  sections = [],
  session,
  targetWindow,
  onComplete
}) => {
  const ref = useRef(null);
  const doneRef = useRef(false);

  const fail = message => {
    if (targetWindow && !targetWindow.closed) {
      targetWindow.document.open();
      targetWindow.document.write(`
        <!doctype html><html><head><title>Note PDF</title>
        <style>
          html,body{margin:0;height:100%}
          body{display:flex;align-items:center;justify-content:center;
            font-family:'Krub',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
            background:#F0F2F5;color:#b3392f;padding:24px;text-align:center}
          p{font-size:14px;font-weight:500}
        </style></head><body><p>${message}</p></body></html>
      `);
      targetWindow.document.close();
    }
    onComplete && onComplete();
  };

  useEffect(() => {
    let cancelled = false;
    const generate = async () => {
      if (cancelled || doneRef.current) return;
      doneRef.current = true;
      await new Promise(resolve => setTimeout(resolve, 400));
      const node = ref.current;
      if (!node) return fail('Failed to render the note.');
      try {
        const canvas = await html2canvas(node, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          windowWidth: RENDER_WIDTH
        });
        const pdf = new jsPDF('p', 'mm', 'letter');
        const pageW = pdf.internal.pageSize.getWidth();
        const pageH = pdf.internal.pageSize.getHeight();

        // Top/bottom margin applied to every page. The tall canvas is sliced
        // per page and each slice is placed inside the printable area, so the
        // margins are consistent across all pages (not just the first/last).
        // 0.6in top/bottom page margin, matching the backend note PDF.
        const marginY = 15.24; // mm
        const imgWidth = pageW; // horizontal margins come from the node padding
        const pxPerMm = canvas.width / imgWidth;
        const pageContentPx = Math.floor((pageH - marginY * 2) * pxPerMm);

        // Read the pixels so we can break pages on blank (whitespace) rows and
        // avoid slicing through a line of text. Falls back to a hard cut if the
        // canvas can't be read.
        let pixels = null;
        try {
          pixels = canvas
            .getContext('2d')
            .getImageData(0, 0, canvas.width, canvas.height).data;
        } catch (e) {
          pixels = null;
        }
        const sampleStep = Math.max(4, Math.floor(canvas.width / 200));
        const isBlankRow = y => {
          if (!pixels) return false;
          const rowStart = y * canvas.width * 4;
          for (let x = 0; x < canvas.width; x += sampleStep) {
            const i = rowStart + x * 4;
            if (pixels[i] < 245 || pixels[i + 1] < 245 || pixels[i + 2] < 245) {
              return false;
            }
          }
          return true;
        };
        // Don't shrink a page below this while hunting for a blank row.
        const minSlicePx = Math.floor(pageContentPx * 0.5);

        let renderedPx = 0;
        let pageIndex = 0;
        while (renderedPx < canvas.height) {
          let sliceHeightPx = Math.min(
            pageContentPx,
            canvas.height - renderedPx
          );

          // Not the last page: back up to the nearest blank row so a line of
          // text isn't split across the page break / margin.
          if (renderedPx + sliceHeightPx < canvas.height && pixels) {
            let cut = renderedPx + sliceHeightPx;
            const limit = renderedPx + minSlicePx;
            while (cut > limit && !isBlankRow(cut)) cut -= 1;
            if (cut > limit) sliceHeightPx = cut - renderedPx;
          }

          const pageCanvas = document.createElement('canvas');
          pageCanvas.width = canvas.width;
          pageCanvas.height = sliceHeightPx;
          const ctx = pageCanvas.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
          ctx.drawImage(
            canvas,
            0,
            renderedPx,
            canvas.width,
            sliceHeightPx,
            0,
            0,
            canvas.width,
            sliceHeightPx
          );

          const sliceData = pageCanvas.toDataURL('image/jpeg', 0.95);
          const sliceHeightMm = sliceHeightPx / pxPerMm;
          if (pageIndex > 0) pdf.addPage();
          pdf.addImage(sliceData, 'JPEG', 0, marginY, imgWidth, sliceHeightMm);

          renderedPx += sliceHeightPx;
          pageIndex += 1;
        }

        const url = URL.createObjectURL(pdf.output('blob'));
        if (targetWindow && !targetWindow.closed) {
          targetWindow.location.href = url;
        } else {
          window.open(url, '_blank', 'noopener');
        }
      } catch (e) {
        console.error('Note PDF generation failed:', e);
        return fail('Failed to build the note PDF.');
      }
      onComplete && onComplete();
    };
    generate();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, []);

  const headerFields = buildNotePdfHeader({ header, patient, session });

  const allSections = (sections || []).filter(
    s => s.heading?.submitToEmr !== false
  );

  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        left: -10000,
        top: 0,
        width: RENDER_WIDTH,
        background: '#ffffff',
        zIndex: -1,
        pointerEvents: 'none'
      }}
    >
      <div ref={ref} style={S.page}>
        <NotePdfHeader fields={headerFields} />

        <div style={{ marginTop: 16 }}>
          {allSections.length ? (
            allSections.map((s, i) => (
                <div key={s.id || i} style={{ marginBottom: 20 }}>
                  <h2 style={S.sectionH}>
                    <span>{s.heading?.customUIHeading || 'Section'}</span>
                  </h2>
                  <div
                    className='ql-editor'
                    style={S.sectionBody}
                    dangerouslySetInnerHTML={{
                      __html: (s.content || [])
                        .map(c => c.value || '')
                        .join('')
                    }}
                  />
                </div>
            ))
          ) : (
            <div style={{ marginBottom: 20 }}>
              <div style={S.sectionBody}>
                No documented sections were returned for this note.
              </div>
            </div>
          )}
        </div>

        <div style={S.footer}>
          <span>{buildNotePdfFooter(headerFields)}</span>
          <span style={{ color: '#6B7A8D', fontWeight: 600 }}>CascadeMD</span>
        </div>
      </div>
    </div>
  );
};

/**
 * Picks the backend note PDF link from GET /clinic/job/attachments: the
 * PROGRESS_NOTE file when there is one, else the PRACTITIONER_NOTE file,
 * else any file link. Returns null when the backend has no file.
 */
export const pickNotePdfLink = list => {
  const files = (list || []).filter(a => a?.fileLink);
  const byType = type => files.find(a => a.noteType === type);
  return (
    (byType('PROGRESS_NOTE') || byType('PRACTITIONER_NOTE') || files[0])
      ?.fileLink || null
  );
};

export default NoteEmrPdf;
